import {
  REMOTE_SETTINGS_OPERATOR_COPY,
  playerSupportsRemoteSettingsReport,
} from "@/features/devices/playerAppMetaSupport";
import type { ConfirmedPlayerSettings } from "@/features/devices/confirmedPlayerSettings";

/** On-open read only. Never includes a settings payload. */
export const REPORT_KK_APP_META_COMMAND = "report_kk_app_meta" as const;

export const REMOTE_SETTINGS_RETRY_LABEL = "Retry";

export type OnOpenRefreshAction =
  | { type: "wait" }
  | { type: "skip"; reason: "no-device" | "commands-disabled" | "already-started" }
  | { type: "unavailable" }
  | { type: "unsupported" }
  | { type: "version-unreported" }
  | { type: "report" };

export type OnOpenRefreshInput = {
  deviceId: string | null;
  commandsEnabled: boolean;
  reportStarted: boolean;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  playerVersion: string | null | undefined;
  /**
   * Complete GET cache is never a reason to skip report_kk_app_meta.
   * Callers pass it so tests can prove the cached snapshot is ignored.
   */
  cachedSnapshotComplete?: boolean;
};

export type RemoteSettingsFormFields = {
  volume: number | null;
  master: number | null;
  appLock: boolean;
  appLockPin: null;
  wakeSleep: boolean;
  startTime: string | null;
  endTime: string | null;
  /** T24 — null means the device has not reported (not confirmed false). */
  sleepSaturday: boolean | null;
  sleepSunday: boolean | null;
};

export function decideOnOpenRefresh(input: OnOpenRefreshInput): OnOpenRefreshAction {
  void input.cachedSnapshotComplete;
  if (!input.deviceId) return { type: "skip", reason: "no-device" };
  if (!input.commandsEnabled) return { type: "skip", reason: "commands-disabled" };
  if (input.reportStarted) return { type: "skip", reason: "already-started" };
  if (input.isFetching) return { type: "wait" };
  if (input.isError) return { type: "unavailable" };
  if (!input.isSuccess) return { type: "wait" };
  const support = playerSupportsRemoteSettingsReport(input.playerVersion);
  if (support === false) return { type: "unsupported" };
  if (support !== true) return { type: "version-unreported" };
  return { type: "report" };
}

export function shouldApplyRefreshResult(args: {
  startedGeneration: number;
  currentGeneration: number;
}): boolean {
  return args.startedGeneration === args.currentGeneration;
}

export function reportKkAppMetaRequest(deviceId: string): {
  deviceId: string;
  command: typeof REPORT_KK_APP_META_COMMAND;
} {
  return { deviceId, command: REPORT_KK_APP_META_COMMAND };
}

export function liveFormFieldsFromSnapshot(
  snap: ConfirmedPlayerSettings
): RemoteSettingsFormFields {
  return {
    volume: snap.volume,
    master: snap.masterLevel,
    appLock: snap.appLock === true,
    appLockPin: null,
    wakeSleep: snap.wakeSleepEnabled === true,
    startTime: snap.startTime,
    endTime: snap.endTime,
    sleepSaturday: typeof snap.sleepSaturday === "boolean" ? snap.sleepSaturday : null,
    sleepSunday: typeof snap.sleepSunday === "boolean" ? snap.sleepSunday : null,
  };
}

export function lastConfirmedStatusCopy(args: {
  syncedAt: string | null | undefined;
  snap?: ConfirmedPlayerSettings | null;
}): string {
  const base = REMOTE_SETTINGS_OPERATOR_COPY.couldNotRead;
  if (!args.syncedAt) {
    return `${base} Last confirmed state is not available.`;
  }
  const details = formatLastConfirmedFields(args.snap ?? null);
  if (details) {
    return `${base} Last confirmed ${args.syncedAt} (not live): ${details}. Confirm is disabled.`;
  }
  return `${base} Last confirmed ${args.syncedAt} (not live). Confirm is disabled.`;
}

export function formatLastConfirmedFields(
  snap: ConfirmedPlayerSettings | null
): string | null {
  if (!snap) return null;
  const bits: string[] = [];
  if (snap.volume != null) bits.push(`Volume ${snap.volume}`);
  if (snap.masterLevel != null) bits.push(`Master ${snap.masterLevel}`);
  if (snap.appLock != null) bits.push(`App Lock ${snap.appLock ? "On" : "Off"}`);
  if (snap.wakeSleepEnabled != null) {
    bits.push(`Wake/Sleep ${snap.wakeSleepEnabled ? "On" : "Off"}`);
  }
  if (typeof snap.sleepSaturday === "boolean") {
    bits.push(`Sleep Sat ${snap.sleepSaturday ? "On" : "Off"}`);
  }
  if (typeof snap.sleepSunday === "boolean") {
    bits.push(`Sleep Sun ${snap.sleepSunday ? "On" : "Off"}`);
  }
  return bits.length ? bits.join(", ") : null;
}

export function canConfirmRemoteSettings(args: {
  commandsEnabled: boolean;
  snapshotComplete: boolean;
  fieldIsTouched: boolean;
  isMutatingUpdate: boolean;
  disabled?: boolean;
}): boolean {
  return (
    Boolean(args.commandsEnabled) &&
    args.snapshotComplete === true &&
    args.fieldIsTouched === true &&
    args.isMutatingUpdate !== true &&
    args.disabled !== true
  );
}
